import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeRowReload } from '../../hooks/useRealtimeRowReload';
import type {
  AssigneeOption,
  CustomAttributeOptionRow,
  CustomAttributeValueRow
} from '../../lib/vm/useDocumentEditorVM';
import type { Database } from '../../types/database';

type DocumentPropertyDefRow =
  Database['public']['Tables']['document_property_defs']['Row'];
type DocumentPropertyOptionRow =
  Database['public']['Tables']['document_property_options']['Row'];

export type DocumentPropertiesLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      defs: DocumentPropertyDefRow[];
      values: CustomAttributeValueRow[];
      optionsByDefId: Map<string, DocumentPropertyOptionRow[]>;
      userOptions: AssigneeOption[];
      /** Property def ids that the currently-selected Subtype option marks as
       *  default ✦ properties. Empty when no subtype is set / subtype def is
       *  missing / the selected option has no defaults. */
      defaultPropIds: Set<string>;
    };

export interface UseDocumentPropertiesParams {
  documentId: string;
  orgId: string;
  onError: (message: string) => void;
}

export function useDocumentProperties({
  documentId,
  orgId,
  onError
}: UseDocumentPropertiesParams) {
  const [load, setLoad] = useState<DocumentPropertiesLoadState>({
    kind: 'loading'
  });

  // Number of commitValue() RPCs currently in flight. A live refresh that fires
  // while the user's own optimistic write has not yet been server-confirmed
  // would re-fetch stale-then-fresh values and could briefly stomp the optimistic
  // value the user just picked — so we defer the refresh until commits settle
  // (mirrors how the body reconcile defers while a save is in flight).
  const pendingCommitsRef = useRef(0);
  const refreshQueuedRef = useRef(false);

  // Compute the ✦ marker set: find the org's Subtype def, find this doc's value
  // (option_id), then read that option's default_property_def_ids. Shared by the
  // full load and the values-only refresh.
  const computeDefaultPropIds = useCallback(
    (
      defs: DocumentPropertyDefRow[],
      values: CustomAttributeValueRow[],
      optionsByDefId: Map<string, DocumentPropertyOptionRow[]>
    ): Set<string> => {
      const defaultPropIds = new Set<string>();
      const subtypeDef = defs.find(
        (d) => d.key === 'subtype' && d.kind === 'single_select'
      );
      if (subtypeDef) {
        const subtypeValue = values.find(
          (v) => v.property_def_id === subtypeDef.id
        );
        const optId =
          subtypeValue && subtypeValue.value
            ? (subtypeValue.value as { option_id?: string }).option_id ?? null
            : null;
        if (optId) {
          const subtypeOpts = optionsByDefId.get(subtypeDef.id) ?? [];
          const selected = subtypeOpts.find((o) => o.id === optId);
          if (selected) {
            for (const id of selected.default_property_def_ids ?? []) {
              defaultPropIds.add(id);
            }
          }
        }
      }
      return defaultPropIds;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const [defsRes, valuesRes, optionsRes, membersRes] = await Promise.all([
        supabase
          .from('document_property_defs')
          .select(
            'id, type_id, org_id, key, kind, label, description, default_value, position, visible_in_list, archived_at, created_at, updated_at, created_by'
          )
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('position', { ascending: true }),
        supabase
          .from('custom_attribute_values')
          .select(
            'id, document_id, property_def_id, org_id, value, created_at, created_by, updated_at, updated_by'
          )
          .eq('document_id', documentId),
        supabase
          .from('document_property_options')
          .select(
            'id, property_def_id, org_id, key, label, position, default_property_def_ids, archived_at, created_at, updated_at, created_by'
          )
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('position', { ascending: true }),
        supabase.rpc('list_org_members', { p_org_id: orgId })
      ]);
      if (cancelled) return;
      const err =
        defsRes.error?.message ??
        valuesRes.error?.message ??
        optionsRes.error?.message ??
        membersRes.error?.message ??
        null;
      if (err) {
        setLoad({ kind: 'error', message: err });
        return;
      }

      // 'subtype' is retired in favour of the first-class document Type
      // (DocumentTypePicker). Filter it out of the rendered property defs so the
      // editor never shows a Subtype pill, independent of whether the DB def has
      // been archived yet. This also makes the ✦ default-properties marker inert
      // (computeDefaultPropIds finds no subtype def → empty set).
      const defs = (
        (defsRes.data ?? []) as DocumentPropertyDefRow[]
      ).filter((d) => d.key !== 'subtype');
      const values = (valuesRes.data ?? []) as CustomAttributeValueRow[];
      const opts = (optionsRes.data ?? []) as DocumentPropertyOptionRow[];

      const optionsByDefId = new Map<string, DocumentPropertyOptionRow[]>();
      for (const opt of opts) {
        const bucket = optionsByDefId.get(opt.property_def_id) ?? [];
        bucket.push(opt);
        optionsByDefId.set(opt.property_def_id, bucket);
      }

      const defaultPropIds = computeDefaultPropIds(defs, values, optionsByDefId);

      type MemberRpcRow = {
        user_id: string | null;
        display_name: string | null;
        username: string | null;
        kind: string | null;
      };
      const userOptions: AssigneeOption[] = Array.isArray(membersRes.data)
        ? (membersRes.data as MemberRpcRow[])
            .filter((m) => m.kind === 'active' && !!m.user_id)
            .map((m) => ({
              user_id: m.user_id!,
              display_name: m.display_name ?? m.username ?? 'Member',
              username: m.username
            }))
            .sort((a, b) => a.display_name.localeCompare(b.display_name))
        : [];

      setLoad({
        kind: 'ready',
        defs,
        values,
        optionsByDefId,
        userOptions,
        defaultPropIds
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, orgId, computeDefaultPropIds]);

  // Re-fetch ONLY the document's custom_attribute_values and reconcile them into
  // the already-loaded panel (defs / options / members are unchanged by a value
  // write). Used by the Realtime subscription so an external/agent attribute
  // edit appears inline without a reopen. Reconciles to server truth; the
  // pendingCommits guard keeps it from fighting the user's own optimistic write.
  const refreshValues = useCallback(async () => {
    if (pendingCommitsRef.current > 0) {
      // A local write is mid-flight — re-run once it settles (commitValue drains
      // the queue) rather than racing it to stale server state.
      refreshQueuedRef.current = true;
      return;
    }
    const { data, error } = await supabase
      .from('custom_attribute_values')
      .select(
        'id, document_id, property_def_id, org_id, value, created_at, created_by, updated_at, updated_by'
      )
      .eq('document_id', documentId);
    if (error) {
      onError(error.message);
      return;
    }
    const values = (data ?? []) as CustomAttributeValueRow[];
    setLoad((prev) => {
      if (prev.kind !== 'ready') return prev;
      return {
        ...prev,
        values,
        defaultPropIds: computeDefaultPropIds(
          prev.defs,
          values,
          prev.optionsByDefId
        )
      };
    });
  }, [documentId, onError, computeDefaultPropIds]);

  // Live attribute refresh: subscribe to custom_attribute_values rows for THIS
  // document (FK filter document_id=eq.<documentId>). Any external/agent write
  // (INSERT/UPDATE) triggers a debounced re-fetch — the same Supabase Realtime
  // mechanism that already refreshes the title/body for the open row.
  useRealtimeRowReload({
    table: 'custom_attribute_values',
    filterColumn: 'document_id',
    rowId: documentId || null,
    onChange: () => {
      void refreshValues();
    }
  });

  const commitValue = useCallback(
    async (propertyDefId: string, value: unknown) => {
      // Route through the save_custom_attribute_value RPC rather than a direct
      // .upsert() on custom_attribute_values. The RPC performs the same upsert
      // but also writes the per-attribute history row and the audit_events
      // property_changed event that powers the Activity tab. A direct table
      // write persists the value but logs nothing.
      //
      // p_expected_updated_at is passed null: this panel does not load the
      // document's last_event_at, so the RPC skips the optimistic-concurrency
      // conflict check (silent-autosave matches the prior direct-write
      // behaviour, which had no conflict check either).
      //
      // Mark a commit in flight so a concurrent Realtime refresh (which fires
      // for our OWN write too) defers until the optimistic value is committed,
      // rather than re-fetching mid-write and briefly stomping it.
      pendingCommitsRef.current += 1;
      let rpcError: string | null = null;
      const { data, error } = await supabase.rpc(
        'save_custom_attribute_value',
        {
          p_document_id: documentId,
          p_attr_def_id: propertyDefId,
          p_value: value as never,
          p_expected_updated_at: null as never
        }
      );
      if (error) {
        rpcError = error.message;
      }
      pendingCommitsRef.current -= 1;
      // Drain a refresh that arrived while this write was in flight — now that
      // the optimistic value is applied, reconciling to server truth is safe.
      if (pendingCommitsRef.current === 0 && refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshValues();
      }
      if (rpcError) {
        onError(rpcError);
        return;
      }
      const envelope = data as
        | { ok: true; updated_at: string; value_id: string; version_number: number }
        | null;
      setLoad((prev) => {
        if (prev.kind !== 'ready') return prev;
        const nextValues = prev.values.filter(
          (v) => v.property_def_id !== propertyDefId
        );
        nextValues.push({
          // The RPC returns the canonical value_id / updated_at; fall back to
          // the prior local row when (defensively) the envelope is absent. The
          // rest of the panel only reads value / property_def_id.
          id:
            envelope?.value_id ??
            prev.values.find((v) => v.property_def_id === propertyDefId)?.id ??
            `__optimistic_${propertyDefId}`,
          document_id: documentId,
          property_def_id: propertyDefId,
          org_id: orgId,
          value: (value ?? null) as never,
          created_at:
            prev.values.find((v) => v.property_def_id === propertyDefId)
              ?.created_at ?? new Date().toISOString(),
          created_by:
            prev.values.find((v) => v.property_def_id === propertyDefId)
              ?.created_by ?? null,
          updated_at: envelope?.updated_at ?? new Date().toISOString(),
          updated_by:
            prev.values.find((v) => v.property_def_id === propertyDefId)
              ?.updated_by ?? null
        });

        // Recompute the ✦ marker set if the Subtype value just changed —
        // changing subtype re-derives defaults, but the underlying values
        // are never destroyed (V2 non-destructive subtype-switch contract).
        let defaultPropIds = prev.defaultPropIds;
        const subtypeDef = prev.defs.find(
          (d) => d.key === 'subtype' && d.kind === 'single_select'
        );
        if (subtypeDef && propertyDefId === subtypeDef.id) {
          defaultPropIds = new Set<string>();
          const optId =
            value && typeof value === 'object'
              ? (value as { option_id?: string }).option_id ?? null
              : null;
          if (optId) {
            const subtypeOpts = prev.optionsByDefId.get(subtypeDef.id) ?? [];
            const selected = subtypeOpts.find((o) => o.id === optId);
            if (selected) {
              for (const id of selected.default_property_def_ids ?? []) {
                defaultPropIds.add(id);
              }
            }
          }
        }

        return { ...prev, values: nextValues, defaultPropIds };
      });
    },
    [documentId, orgId, onError, refreshValues]
  );

  const createOption = useCallback(
    async (
      propertyDefId: string,
      label: string
    ): Promise<CustomAttributeOptionRow | null> => {
      const trimmed = label.trim();
      if (!trimmed) return null;
      const { data, error } = await supabase
        .rpc('create_custom_attribute_option', {
          p_attr_def_id: propertyDefId,
          p_label: trimmed
        });
      if (error) {
        onError(error.message);
        return null;
      }
      const envelope = data as
        | { ok: true; option: CustomAttributeOptionRow }
        | { ok: false; error: string }
        | null;
      if (!envelope || envelope.ok !== true) {
        onError(
          (envelope && 'error' in envelope ? envelope.error : null) ??
            'Could not create option'
        );
        return null;
      }
      const newOpt = envelope.option;
      // Mirror into local option cache so the new option is selectable
      // immediately without a refetch.
      setLoad((prev) => {
        if (prev.kind !== 'ready') return prev;
        const nextOpts = new Map(prev.optionsByDefId);
        const bucket = nextOpts.get(propertyDefId) ?? [];
        nextOpts.set(propertyDefId, [
          ...bucket,
          newOpt as DocumentPropertyOptionRow
        ]);
        return { ...prev, optionsByDefId: nextOpts };
      });
      return newOpt;
    },
    [onError]
  );

  return { load, commitValue, createOption, refresh: refreshValues };
}
